// src/pages/BookingSuccess.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { createOrGetInquiryThread } from "../firebase";

// Firestore fallback (only used if your firebase.js doesn't expose helpers)
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";

/** ----------------------------- Helpers ----------------------------- **/
const currency = (n) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n ?? 0);

const pad2 = (n) => String(n).padStart(2, "0");
const parseYYYYMMDD = (s) => {
  const [Y, M, D] = String(s || "").split("-").map(Number);
  if (!Y || !M || !D) return null;
  return new Date(Y, M - 1, D);
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + Number(n || 0));
  return x;
};
const toYYYYMMDD = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
};
const fmtHour = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n) || n < 0 || n > 23) return null;
  const hour = n % 12 || 12;
  const ampm = n >= 12 ? "PM" : "AM";
  return `${hour} ${ampm}`;
};

// ICS helpers (DTEND exclusive per RFC 5545)
function icsDateUTC(dateObj, hour = null) {
  const d = new Date(dateObj);
  if (hour == null) {
    return `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}`;
  }
  const dt = new Date(
    Date.UTC(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      Math.max(0, Math.min(23, hour)),
      0,
      0
    )
  );
  return `${dt.getUTCFullYear()}${pad2(dt.getUTCMonth() + 1)}${pad2(
    dt.getUTCDate()
  )}T${pad2(dt.getUTCHours())}${pad2(dt.getUTCMinutes())}00Z`;
}
function buildIcs({ summary, description, location, start, end, startHour, endHour }) {
  const isTimed = Number.isFinite(Number(startHour)) || Number.isFinite(Number(endHour));
  const uid =
    (typeof crypto !== "undefined" && crypto?.randomUUID?.()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}@unionhall`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Union Hall//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z")}`,
    `SUMMARY:${(summary || "Booking").replace(/\r?\n/g, " ")}`,
    `DESCRIPTION:${(description || "").replace(/\r?\n/g, " ")}`,
    location ? `LOCATION:${location.replace(/\r?\n/g, " ")}` : "",
    isTimed
      ? `DTSTART:${icsDateUTC(parseYYYYMMDD(start), startHour)}`
      : `DTSTART;VALUE=DATE:${icsDateUTC(parseYYYYMMDD(start))}`,
    isTimed
      ? `DTEND:${icsDateUTC(parseYYYYMMDD(end), endHour)}`
      : `DTEND;VALUE=DATE:${icsDateUTC(parseYYYYMMDD(end))}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);

  return lines.join("\r\n");
}

/** ---------------------- Robust photo resolution --------------------- **/
function looksLikeUrl(u) {
  return typeof u === "string" && /^https?:\/\//i.test(u);
}
function urlFromObj(o) {
  if (!o || typeof o !== "object") return null;
  return (
    o.url ||
    o.src ||
    o.downloadURL ||
    o.downloadUrl ||
    o.imageUrl ||
    o.photoUrl ||
    null
  );
}
function firstValidUrl(arr) {
  if (!Array.isArray(arr)) return null;
  for (const item of arr) {
    if (looksLikeUrl(item)) return item;
    const u = urlFromObj(item);
    if (looksLikeUrl(u)) return u;
  }
  return null;
}
function getPrimaryPhoto(listing, booking) {
  const bookingCandidates = [booking?.listingPhoto, booking?.listingImage];
  for (const c of bookingCandidates) if (looksLikeUrl(c)) return c;

  const singleCandidates = [
    listing?.coverPhoto,
    listing?.cover_image?.url,
    listing?.coverImage?.url,
    listing?.photo,
    listing?.photoUrl,
    listing?.photoURL,
    listing?.image,
    listing?.imageUrl,
    listing?.heroImage?.url,
    listing?.heroImage?.src,
  ];
  for (const c of singleCandidates) if (looksLikeUrl(c)) return c;

  const arrayCandidates = [
    listing?.photos,
    listing?.images,
    listing?.gallery,
    listing?.media,
    listing?.photosUrls,
  ];
  for (const arr of arrayCandidates) {
    const u = firstValidUrl(arr);
    if (looksLikeUrl(u)) return u;
  }

  return "https://images.unsplash.com/photo-1505692794403-34d4982f88aa?q=80&w=800&auto=format&fit=crop";
}

/** =============================== Page =============================== **/
export default function BookingSuccess() {
  const { state } = useLocation();
  const { bookingId: routeBookingId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const bookingId =
    state?.bookingId || state?.booking?.id || params.get("bid") || routeBookingId || "";

  const [booking, setBooking] = useState(state?.booking || null);
  const [listing, setListing] = useState(state?.listing || null);
  const [loading, setLoading] = useState(!(state?.booking && state?.listing));
  const [error, setError] = useState("");

  // NEW: live countdown to approval expiry (if manual capture)
  const [timeLeftMs, setTimeLeftMs] = useState(null);

  // Wait for Auth (prevents permission-denied right after Stripe redirect)
  async function waitForAuthInit(a) {
    if (a.currentUser) return;
    await new Promise((resolve) => {
      const unsub = onAuthStateChanged(
        a,
        () => {
          unsub();
          resolve();
        },
        () => {
          unsub();
          resolve();
        }
      );
    });
  }

  // Live subscribe to booking doc (so paid/authorized -> paid updates reflect immediately)
  useEffect(() => {
    if (!bookingId) return;
    const db = getFirestore();
    const ref = doc(db, "bookings", String(bookingId));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) setBooking({ id: snap.id, ...snap.data() });
      },
      // ignore permission errors here; initial load handles them
      () => {}
    );
    return () => unsub();
  }, [bookingId]);

  // Load booking → listing (sequential) if needed
  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        if (!bookingId) throw new Error("Missing booking id.");

        await waitForAuthInit(auth);

        // 1) Booking
        let b = booking;
        if (!b) {
          const mod = await import("../firebase").catch(() => ({}));
          if (typeof mod.getBookingById === "function") {
            b = await mod.getBookingById(bookingId);
          } else {
            const db = getFirestore();
            const snap = await getDoc(doc(db, "bookings", String(bookingId)));
            b = snap.exists() ? { id: snap.id, ...snap.data() } : null;
          }
          if (!alive) return;
          if (!b) throw new Error("We couldn’t find this booking.");
          setBooking(b);
        }

        // 2) Listing (gracefully tolerate permission-denied/unpublished)
        if (!listing && (b?.listingId || state?.booking?.listingId)) {
          const lid = b?.listingId || state?.booking?.listingId;
          let l = null;
          try {
            const mod = await import("../firebase").catch(() => ({}));
            if (typeof mod.getListingById === "function") {
              l = await mod.getListingById(lid);
            } else {
              const db = getFirestore();
              const snap = await getDoc(doc(db, "listings", String(lid)));
              l = snap.exists() ? { id: snap.id, ...snap.data() } : null;
            }
          } catch (le) {
            // If listing is unpublished and rules block, just fall through without erroring the page.
            l = null;
          }
          if (!alive) return;
          setListing(l || null);
        }
      } catch (e) {
        console.error(e);
        if (alive) setError(e?.message || "Couldn’t load your booking.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (!state?.booking || !state?.listing) load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  /** ----------------------------- Derived ----------------------------- **/
  const mode = (booking?.mode || "").toLowerCase(); // "day" | "overnight"
  const isOvernight =
    mode === "overnight" ||
    (booking?.startDate && (booking?.endDateExclusive || booking?.endDate));

  const dayDate = booking?.date || booking?.startDate || state?.date || null;

  const startDate = isOvernight ? booking?.startDate : dayDate;
  const endDateExclusive = isOvernight
    ? booking?.endDateExclusive || booking?.endDate
    : dayDate
    ? toYYYYMMDD(addDays(parseYYYYMMDD(dayDate), 1))
    : null;

  const nights =
    isOvernight && startDate && endDateExclusive
      ? Math.max(
          0,
          Math.round(
            (parseYYYYMMDD(endDateExclusive) - parseYYYYMMDD(startDate)) / 86400000
          )
        )
      : 0;

  const cityState = [listing?.city, listing?.stateCode ?? listing?.state]
    .filter(Boolean)
    .join(", ");
  const title = listing?.title || "Your booking";
  const checkInHour = Number.isFinite(Number(listing?.checkInHour))
    ? Number(listing.checkInHour)
    : null;
  const checkOutHour = Number.isFinite(Number(listing?.checkOutHour))
    ? Number(listing.checkOutHour)
    : null;

  // Money (server-authoritative cents)
  const subtotalCents = Number.isFinite(Number(booking?.amountSubtotalCents))
    ? Number(booking.amountSubtotalCents)
    : 0;
  const platformFeeCents = Number.isFinite(Number(booking?.platformFeeCents))
    ? Number(booking.platformFeeCents)
    : 0;
  const totalCents = Number.isFinite(Number(booking?.amountTotalCents))
    ? Number(booking.amountTotalCents)
    : subtotalCents + platformFeeCents;

  // ICS (show only on confirmed bookings)
  const icsUrl = useMemo(() => {
    if (!startDate || !endDateExclusive) return null;
    const summary = `${title} — ${isOvernight ? "Stay" : "Day booking"}`;
    const description = isOvernight
      ? `Check-in ${fmtHour(checkInHour) || ""} • Check-out ${
          fmtHour(checkOutHour) || ""
        }`.trim()
      : "Day use booking";
    const location =
      [
        listing?.addressLine1,
        listing?.city,
        listing?.stateCode || listing?.state,
        listing?.zip,
      ]
        .filter(Boolean)
        .join(", ") || cityState || "";

    const ics = buildIcs({
      summary,
      description,
      location,
      start: startDate,
      end: endDateExclusive,
      startHour: isOvernight ? checkInHour : null,
      endHour: isOvernight ? checkOutHour : null,
    });
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    return URL.createObjectURL(blob);
  }, [title, listing, startDate, endDateExclusive, isOvernight, checkInHour, checkOutHour, cityState]);

  useEffect(
    () => () => {
      if (icsUrl) URL.revokeObjectURL(icsUrl);
    },
    [icsUrl]
  );

  const niceLocation = useMemo(() => {
    return (
      listing?.addressLine1 ||
      [listing?.city, listing?.stateCode || listing?.state].filter(Boolean).join(", ") ||
      ""
    );
  }, [listing]);
  const mapsHref = niceLocation
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        niceLocation
      )}`
    : null;

  const status = (booking?.status || state?.status || "paid").toLowerCase();
  const receiptUrl = booking?.stripeReceiptUrl || null;

  const photoUrl = useMemo(() => getPrimaryPhoto(listing, booking), [listing, booking]);

  // Approval flow flags (handles both "pending" and "authorized" from webhook)
  const requiresApproval =
    booking?.requiresApproval === true || listing?.instabook === false;
  const isConfirmed = status === "paid";
  const isAuthorized = status === "authorized";
  const isPendingApproval =
    (requiresApproval &&
      (isAuthorized || booking?.approvalStatus === "pending")) ||
    (status === "pending" && requiresApproval);
  const isPendingPayment = status === "pending" && !requiresApproval;

  // Approval countdown from approvalExpiresAt (Firestore Timestamp or millis)
  useEffect(() => {
    if (!isPendingApproval) {
      setTimeLeftMs(null);
      return;
    }
    const ts = booking?.approvalExpiresAt;
    const expiresAtMs =
      (ts && typeof ts.toMillis === "function" && ts.toMillis()) ||
      (typeof ts === "number" ? ts : null);
    if (!expiresAtMs) {
      setTimeLeftMs(null);
      return;
    }
    const tick = () => setTimeLeftMs(Math.max(0, expiresAtMs - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isPendingApproval, booking?.approvalExpiresAt]);

  function fmtCountdown(ms) {
    if (!Number.isFinite(ms)) return null;
    const s = Math.floor(ms / 1000);
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
  }

  // Access details (per-booking; only shown for paid bookings)
  const access = {
    summary: booking?.accessSummary || "",
    guide: booking?.checkInGuide || booking?.accessInstructions || "",
    doorCode: booking?.doorCode || "",
    lockboxLocation: booking?.lockboxLocation || "",
    wifiNetwork: booking?.wifiNetwork || "",
    wifiPassword: booking?.wifiPassword || "",
    parkingNote: booking?.parkingNote || "",
  };
  const hasAnyAccess =
    !!(
      access.summary ||
      access.guide ||
      access.doorCode ||
      access.lockboxLocation ||
      access.wifiNetwork ||
      access.wifiPassword ||
      access.parkingNote
    );

  /** ----------------------------- Actions ----------------------------- **/
  async function onMessageNow() {
    const me = auth.currentUser;
    const hostUid =
      booking?.hostUid ||
      listing?.hostUid ||
      listing?.ownerUid ||
      listing?.uid ||
      listing?.userId ||
      listing?.user?.uid ||
      null;

    if (!me || !listing?.id || !hostUid) return navigate("/messages");
    try {
      const t = await createOrGetInquiryThread({
        listingId: listing.id,
        hostUid,
        guestUid: me.uid,
        bookingId: bookingId || booking?.id,
        listingTitle: listing.title,
        listingPhoto: photoUrl,
      });
      navigate(`/messages?thread=${t.id}`);
    } catch {
      navigate("/messages");
    }
  }
  async function onShare() {
    try {
      const url = window.location.href;
      const titleShare = "My Union Hall booking";
      const text = listing?.title
        ? `I just booked ${listing.title}${
            startDate ? ` for ${startDate}` : ""
          }.`
        : `I just booked a space${
            startDate ? ` for ${startDate}` : ""
          }.`;
      if (navigator.share) {
        await navigator.share({ title: titleShare, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard");
      }
    } catch {}
  }
  const onPrint = () => window.print();

  /** ----------------------------- Early UI ---------------------------- **/
  if (!bookingId && !state?.booking) {
    return (
      <main className="mx-auto max-w-xl px-4 py-16">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-xl font-semibold">Booking details</h1>
          <p className="mt-2 text-neutral-600">
            We didn’t get a booking reference. Check your Trips for the latest.
          </p>
          <div className="mt-4 flex justify-center gap-3">
            <Link
              to="/trips"
              className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white"
            >
              View Trips
            </Link>
            <Link
              to="/explore"
              className="rounded-full border border-neutral-300 px-4 py-2 text-sm"
            >
              Explore
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-64 rounded bg-neutral-200" />
            <div className="h-24 rounded-xl bg-neutral-200" />
          </div>
        </div>
      </main>
    );
  }

  if (error || !booking) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-semibold">We couldn’t load your booking</h1>
          <p className="mt-2 text-neutral-600">
            {error || "Please try again from your Trips."}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link
              to="/trips"
              className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white"
            >
              View Trips
            </Link>
            <Link
              to="/explore"
              className="rounded-full border border-neutral-300 px-4 py-2 text-sm"
            >
              Explore
            </Link>
          </div>
        </div>
      </main>
    );
  }

  /** ------------------------------- View ------------------------------ **/
  return (
    <main className="mx-auto max-w-3xl px-4 py-12 print:px-0 print:py-4">
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm print:border-0 print:shadow-none">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">
              {isConfirmed
                ? "Booking confirmed 🎉"
                : isPendingApproval
                ? "Request sent — awaiting host approval"
                : isPendingPayment
                ? "Booking received"
                : "Booking received"}
            </h1>
            <p className="mt-1 text-neutral-600">
              {isConfirmed
                ? "We’ve sent a confirmation to your email."
                : isPendingApproval
                ? `We saved your card but haven’t charged it. If the host approves, we’ll charge ${currency(
                    totalCents / 100
                  )} automatically and email you a receipt. If they decline or don’t respond, your card won’t be charged.`
                : status === "pending"
                ? "Your payment is processing. We’ll email you once it clears."
                : "Thanks! We’ve recorded your booking."}
            </p>

            {isPendingApproval && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <div className="font-medium">Waiting on host approval</div>
                <div className="mt-1">
                  {timeLeftMs != null ? (
                    <>
                      Approval window ends in{" "}
                      <span className="font-semibold tabular-nums">
                        {fmtCountdown(timeLeftMs)}
                      </span>
                      .
                    </>
                  ) : (
                    <>
                      We’ll notify you as soon as the host decides. You can message them below
                      if you need a quick answer.
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="hidden print:block text-right text-xs text-neutral-500">
            <div>Union Hall</div>
            <div>unionhall.app</div>
          </div>
        </div>

        {/* Summary card */}
        <div className="mt-6 flex items-start gap-3">
          <div className="h-16 w-16 overflow-hidden rounded-lg border border-neutral-200">
            <img
              src={photoUrl}
              alt={listing?.title || "Listing"}
              className="h-full w-full object-cover"
              loading="eager"
              referrerPolicy="no-referrer"
            />
          </div>

          <div className="min-w-0">
            <div className="truncate font-semibold">
              {listing?.title || "Your booked space"}
            </div>
            <div className="truncate text-neutral-600">{cityState || "—"}</div>

            {isOvernight ? (
              <>
                <div className="mt-1 text-neutral-500">
                  Check-in:{" "}
                  <span className="font-medium text-neutral-700">
                    {startDate || "—"}{" "}
                    {fmtHour(checkInHour) ? `• ${fmtHour(checkInHour)}` : ""}
                  </span>
                </div>
                <div className="text-neutral-500">
                  Check-out:{" "}
                  <span className="font-medium text-neutral-700">
                    {endDateExclusive || "—"}{" "}
                    {fmtHour(checkOutHour) ? `• ${fmtHour(checkOutHour)}` : ""}
                  </span>
                </div>
                <div className="text-neutral-500">
                  Nights:{" "}
                  <span className="font-medium text-neutral-700">
                    {nights || "—"}
                  </span>
                </div>
              </>
            ) : (
              <div className="mt-1 text-neutral-500">
                Date:{" "}
                <span className="font-medium text-neutral-700">
                  {startDate || "—"}
                </span>
              </div>
            )}

            <div className="text-neutral-500">
              Guests:{" "}
              <span className="font-medium text-neutral-700">
                {booking?.guests || 1}
              </span>
            </div>

            <div className="mt-2 font-medium">
              Total: {currency(totalCents / 100)}
            </div>

            {niceLocation && (
              <div className="mt-1 text-sm text-neutral-600">
                Location:{" "}
                <span className="text-neutral-800">{niceLocation}</span>{" "}
                {mapsHref && (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-1 underline"
                  >
                    (directions)
                  </a>
                )}
              </div>
            )}

            {/* IDs */}
            <div className="mt-2 text-xs text-neutral-500">
              Booking ID:{" "}
              <span className="font-mono">
                {bookingId || booking?.id || "—"}
              </span>
              {booking?.stripePaymentIntentId && (
                <>
                  {" · "}Payment Intent:{" "}
                  <span className="font-mono">
                    {booking.stripePaymentIntentId}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-2">
          <Link
            to="/trips"
            className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 font-semibold hover:bg-neutral-50"
          >
            View in Trips
          </Link>

          <button
            onClick={onMessageNow}
            className="inline-flex items-center justify-center rounded-xl bg-black px-4 py-2 font-semibold text-white hover:bg-[#B8860B]"
          >
            Message your host
          </button>

          {isConfirmed && icsUrl && (
            <a
              href={icsUrl}
              download={`UnionHall-${(listing?.title || "booking").slice(
                0,
                24
              )}-${startDate}.ics`}
              className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 font-semibold hover:bg-neutral-50"
            >
              Add to Calendar
            </a>
          )}

          <button
            onClick={onShare}
            className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 font-semibold hover:bg-neutral-50"
          >
            Share booking
          </button>

          <button
            onClick={onPrint}
            className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 font-semibold hover:bg-neutral-50"
          >
            Print
          </button>

          {receiptUrl && (
            <a
              href={receiptUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 font-semibold hover:bg-neutral-50"
            >
              View receipt
            </a>
          )}
        </div>

        {/* Check-in & access – ONLY for paid bookings */}
        {isConfirmed && (
          <section className="mt-8 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
            <h3 className="text-base font-semibold">Check-in & access</h3>

            {!hasAnyAccess ? (
              <p className="mt-2 text-sm text-neutral-700">
                Your host will share door codes, parking details, and Wi-Fi
                closer to your stay. You’ll always find the latest instructions
                in{" "}
                <Link to="/trips" className="underline">
                  Trips
                </Link>{" "}
                and in your messages with the host.
              </p>
            ) : (
              <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                {access.summary && (
                  <div className="rounded-lg border border-neutral-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                      Overview
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-neutral-800">
                      {access.summary}
                    </p>
                  </div>
                )}

                {(access.doorCode || access.lockboxLocation) && (
                  <div className="rounded-lg border border-neutral-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                      Door / lockbox
                    </div>
                    {access.doorCode && (
                      <p className="mt-1 font-semibold text-neutral-900">
                        Code:{" "}
                        <span className="font-mono tracking-wider">
                          {access.doorCode}
                        </span>
                      </p>
                    )}
                    {access.lockboxLocation && (
                      <p className="mt-1 text-neutral-800">
                        Location: {access.lockboxLocation}
                      </p>
                    )}
                    <p className="mt-2 text-[11px] text-neutral-500">
                      For safety, these codes are intended only for this
                      booking. Hosts are encouraged to change codes between
                      guests.
                    </p>
                  </div>
                )}

                {(access.wifiNetwork || access.wifiPassword) && (
                  <div className="rounded-lg border border-neutral-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                      Wi-Fi
                    </div>
                    {access.wifiNetwork && (
                      <p className="mt-1 text-neutral-800">
                        Network:{" "}
                        <span className="font-mono">
                          {access.wifiNetwork}
                        </span>
                      </p>
                    )}
                    {access.wifiPassword && (
                      <p className="mt-1 text-neutral-800">
                        Password:{" "}
                        <span className="font-mono">
                          {access.wifiPassword}
                        </span>
                      </p>
                    )}
                  </div>
                )}

                {access.guide && (
                  <div className="md:col-span-2 rounded-lg border border-neutral-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                      Step-by-step check-in
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-neutral-800">
                      {access.guide}
                    </p>
                  </div>
                )}

                {access.parkingNote && (
                  <div className="md:col-span-2 rounded-lg border border-neutral-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                      Parking
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-neutral-800">
                      {access.parkingNote}
                    </p>
                  </div>
                )}
              </div>
            )}

            <p className="mt-3 text-xs text-neutral-500">
              Never share door codes or access instructions outside of Union
              Hall or messages with your host.
            </p>
          </section>
        )}

        {/* Info blocks */}
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <section className="rounded-xl border border-neutral-200 p-4">
            <h3 className="text-base font-semibold">What’s next</h3>
            {isPendingApproval ? (
              <ul className="mt-2 list-inside list-disc text-sm text-neutral-700">
                <li>
                  The host reviews your request. We’ll email you when they
                  approve or decline.
                </li>
                <li>
                  Your card is only on hold. If approved, we’ll charge{" "}
                  {currency(totalCents / 100)} automatically.
                </li>
                <li>Need a faster answer? Message your host.</li>
                <li>
                  Once approved and paid, full check-in and access details will
                  appear on your{" "}
                  <Link to="/trips" className="underline">
                    Trips
                  </Link>{" "}
                  page and in your messages with the host.
                </li>
              </ul>
            ) : (
              <ul className="mt-2 list-inside list-disc text-sm text-neutral-700">
                <li>
                  Check your messages for any arrival instructions from the
                  host.
                </li>
                <li>
                  Bring a valid ID and your booking ID{" "}
                  <span className="font-mono">
                    {bookingId || booking?.id || "—"}
                  </span>
                  .
                </li>
                <li>
                  Need to change the dates or guest count? Message your host.
                </li>
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-neutral-200 p-4">
            <h3 className="text-base font-semibold">Cancellation policy</h3>
            <p className="mt-2 text-sm text-neutral-700">
              Full refund up to 7 days before{" "}
              {isOvernight ? "check-in" : "the event"}. Within 7 days,
              changes and cancellations require host approval.
            </p>
          </section>
        </div>

        {/* Support / fallback */}
        <div className="mt-6 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
          Having trouble or didn’t get an email?{" "}
          <Link to="/help" className="underline">
            Visit Help
          </Link>{" "}
          or message your host. If you contact support, include your Booking
          ID.
        </div>

        {loading && (
          <div className="mt-6 text-sm text-neutral-600">
            Loading your booking…
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </main>
  );
}
